import {
  faFaceFrown,
  faFolderOpen,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useCallback, useMemo } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useMutation } from "urql";

import { ContainerLoader } from "@/components/ui-v2/ContainerLoader/ContainerLoader";
import { useFilePickerState } from "@/components/ui-v2/FilePicker/state";
import type {
  FilepickerFile,
  Source,
} from "@/components/ui-v2/FilePicker/types";
import { Icon } from "@/components/ui-v2/Icon/Icon";
import { useSnackbar } from "@/components/ui-v2/Snackbar/useSnackbar";
import { StateCard } from "@/components/ui-v2/StateCard/StateCard";
import { UploadButton } from "@/components/ui-v2/UploadButton/UploadButton";
import {
  useBasicData,
  useQueryWithResponseHandler,
  useUrqlResponseHandler,
} from "@/hooks";
import { ContentType } from "@/types";
import { getObjectType } from "@/utils/libraryObjectUtils";
import { getConnectionNodesTyped } from "@/utils/relay";
import { getModifier, Modifier, selectInList } from "@/utils/selectInList";

import { GridContainer } from "../GridContainer/GridContainer";
import { ClearRecentFiles, GetRecentFiles } from "./queries.graphql";
import { RecentTile, RecentTileAction } from "./RecentTile";

export const Recent = () => {
  const { showError } = useSnackbar();
  const theme = useTheme();
  const { workspace } = useBasicData();

  const { getResponseFromState } = useUrqlResponseHandler();

  const {
    selectedFiles,
    maxFiles,
    acceptedFileTypes,
    modality,
    setSelectedFile,
    setIsUploading,
    setSelectedFiles,
    setSelectionAsConfirmed,
  } = useFilePickerState();

  const [clearRecentFilesResult, clearRecentFiles] =
    useMutation(ClearRecentFiles);

  const filtering = useMemo(() => {
    const filters = [
      {
        field: "interactedAt",
        operator: "isnull",
        value: "false",
      },
    ];
    if (acceptedFileTypes && Object.keys(acceptedFileTypes).length > 0) {
      const contentTypes = JSON.stringify(Object.keys(acceptedFileTypes));
      filters.push({
        field: "content_type",
        operator: "in",
        value: contentTypes,
      });
    }

    return filters;
  }, [acceptedFileTypes]);

  const query = useQueryWithResponseHandler({
    query: GetRecentFiles,
    variables: {
      workspaceName: workspace?.name,
      numResults: 28, // Standard displays 7 columns, so 28 files is 4 rows
      filtering,
    },
    pause: !acceptedFileTypes,
  });

  const files =
    query.data?.workspace?.__typename === "Workspace"
      ? getConnectionNodesTyped(query.data.workspace.files)
      : [];

  const formattedFiles: FilepickerFile[] = files.map(
    (file) =>
      ({
        id: file.id,
        src: file.url,
        name: file.name,
        type: getObjectType(file),
        contentType: file.contentType as ContentType,
        thumbnails: file.thumbnails,
        interactedAt: new Date(file.interactedAt ?? Date.now()),
        source: "recent" as Source,
        width: file.metadata?.width,
        height: file.metadata?.height,
        durationSeconds: file.metadata?.durationSeconds,
        thumbnailSrc: file.thumbnails?.small?.url ?? file.url,
      }) as FilepickerFile,
  );

  const handleUploadStart = () => {
    setIsUploading(true);
  };

  const handleUploadSuccess = (files: FilepickerFile[]) => {
    setIsUploading(false);
    setSelectedFiles(files);
    setSelectionAsConfirmed();
  };

  const handleSelectFiles = useCallback(
    (
      item: FilepickerFile | null,
      allItems: FilepickerFile[],
      modifier?: Modifier,
    ) => {
      if (!item) {
        setSelectedFiles([]);
        return;
      }

      const allIds = new Set<string>(allItems.map((item) => item.id));

      const selectedIds = new Set<string>(
        selectedFiles?.map((item) => item.id),
      );

      const nextSelectedIds = selectInList<string>(
        item.id,
        allIds,
        selectedIds,
        modifier,
      );

      const nextSelectedItems = allItems.filter((item) =>
        nextSelectedIds.has(item.id),
      );

      setSelectedFiles(nextSelectedItems);
    },
    [setSelectedFiles, maxFiles, selectedFiles],
  );

  const handleSelect = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>, item: FilepickerFile) => {
      event.stopPropagation();
      event.preventDefault();

      const modifier = getModifier(event);
      handleSelectFiles(item, formattedFiles, modifier ?? undefined);
    },
    [formattedFiles, handleSelectFiles],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedFiles(formattedFiles);
  }, [formattedFiles, setSelectedFiles]);

  const handleDoubleClick = (file: FilepickerFile) => {
    const selected = selectedFiles.some(
      (selectedFile) => file.id === selectedFile.id,
    );

    if (!selected) {
      setSelectedFile(file);
    }

    setSelectionAsConfirmed();
  };

  const handleClearRecents = useCallback(async () => {
    const result = await clearRecentFiles({
      input: {
        workspaceId: workspace?.id ?? "",
      },
    });

    const { error } = getResponseFromState({
      state: result,
    });

    if (error) {
      showError("Failed to clear recent files");
    } else {
      query.execute();
    }
  }, [clearRecentFiles, workspace?.id, getResponseFromState, showError]);

  const handleAction = (
    event: React.MouseEvent,
    action: RecentTileAction,
    item: FilepickerFile,
  ) => {
    switch (action) {
      case "SELECT":
        handleSelect(event, item);
        break;
      case "DOUBLE_CLICK":
        handleDoubleClick(item);
        break;
      default:
        break;
    }
  };

  // Handle select all (mod + a)
  useHotkeys(
    "mod+a",
    handleSelectAll,
    {
      preventDefault: true,
    },
    [handleSelectAll],
  );

  if (query.state.fetching) {
    return <ContainerLoader />;
  }

  if (query.state.error) {
    return (
      <StateCard
        icon={<Icon size="large" icon={faFaceFrown} />}
        title="Unable to load recent files"
        description="Please check your internet connection and try again."
      />
    );
  }

  if (query.data?.workspace?.__typename !== "Workspace") {
    return null;
  }

  if (formattedFiles.length === 0) {
    return (
      <StateCard
        icon={<Icon size="large" icon={faFolderOpen} />}
        title="No recent files"
        description="Files you've recently interacted with will appear here."
        fill
        actions={
          <UploadButton
            variant="outlined"
            sourceType="REFERENCE"
            onUploadStart={handleUploadStart}
            onUploadSuccess={handleUploadSuccess}
            accept={acceptedFileTypes}
            maxFiles={maxFiles}
            modality={modality}
            fullWidth={false}
          />
        }
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
        height: "100%",
      }}
    >
      <GridContainer sx={{ p: 2 }}>
        {formattedFiles.map((file) => {
          const isSelected = selectedFiles?.some(
            (selectedFile) => selectedFile.id === file.id,
          );

          return (
            <RecentTile
              key={file.id}
              file={file}
              isSelected={isSelected}
              onAction={handleAction}
            />
          );
        })}
      </GridContainer>
      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          py: theme.spacing(4),
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={handleClearRecents}
          loading={clearRecentFilesResult.fetching}
          startIcon={<Icon icon={faTrash} />}
        >
          Clear Recents
        </Button>
      </Stack>
    </Box>
  );
};
